import {
  Button,
  FilterBar,
  type FilterField,
  formatMoney,
  ListView,
  type ListViewDef,
  PageHeader,
  type Search,
  useCursorList,
  ViewTabs,
} from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import DropdownMenu from "@majitap/ui/arc/dropdown-menu/dropdown-menu";
import { useQueries, useQuery, useSuspenseQuery } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { ArrowDownUp, CircleDot, Clock, TriangleAlert, Users, Wallet } from "lucide-react";
import { type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { api, errorOf, ok } from "../../api";
import { useCompanyFormat } from "../../company";
import { deliveryHref } from "../../deliveries";
import { messageFor } from "../../error-copy";
import { DELIVERY_STATUS, PAYMENT_STATUS } from "../../labels";
import { datesField, daysRange, optionsOf, useFleetFields, useSavedViews, viewOf } from "../../list-filters";
import { filterSearch } from "../../list-search";
import { meQuery } from "../../session";
import styles from "../orders/orders.module.css";
import { PayerFilterDialog } from "../shared/payer-filter";

/** Every filter the list reads from the URL (P8-57); the API checks the values. */
export const deliveryFilters = filterSearch({
  q: "text",
  status: "text",
  not_status: "text",
  from: "text",
  to: "text",
  zone: "text",
  not_zone: "text",
  driver: "text",
  not_driver: "text",
  truck: "text",
  not_truck: "text",
  fillsite: "text",
  not_fillsite: "text",
  customer_id: "text",
  business_id: "text",
  payment: "text",
  not_payment: "text",
  late: ["true"] as const,
  incident: ["open"] as const,
  sort: ["scheduled", "status", "newest", "oldest"] as const,
});
type DeliverySearch = ReturnType<typeof deliveryFilters.validate>;

const FILTER_KEYS = [
  "q",
  "status",
  "not_status",
  "from",
  "to",
  "zone",
  "not_zone",
  "driver",
  "not_driver",
  "truck",
  "not_truck",
  "fillsite",
  "not_fillsite",
  "customer_id",
  "business_id",
  "payment",
  "not_payment",
  "late",
  "incident",
];

const SORT = { scheduled: "Scheduled time", status: "Status", newest: "Newest first", oldest: "Oldest first" } as const;
const ICON = { size: 15, "aria-hidden": true } as const;

/** The views every staff member has. The first opens when the URL has no filters. */
function builtInViews(timezone: string): ListViewDef[] {
  return [
    { id: "pending", label: "Pending", search: { status: "pending,fillsite_assigned" } },
    {
      id: "ongoing",
      label: "Ongoing",
      search: { status: "assigned,accepted,at_fillsite,in_transit,at_customer,suspended", sort: "status" },
    },
    { id: "late", label: "Late", search: { late: "true" } },
    { id: "failed", label: "Failed · last 7 days", search: { status: "failed", ...daysRange(timezone, 7, 6) } },
    { id: "delivered", label: "Delivered", search: { status: "delivered" } },
    { id: "cancelled", label: "Cancelled", search: { status: "cancelled" } },
    { id: "all", label: "All deliveries", search: {} },
  ];
}

const summaryQuery = (search: Search) => ({
  queryKey: ["deliveries", "summary", search],
  staleTime: 30_000,
  queryFn: () => ok(api.GET("/v1/admin/dispatch/deliveries/summary", { params: { query: search as never } })),
});

/** "09:30", or "21 Oct, 09:30" on another day, in the company's zone. */
function when(iso: string, timezone: string) {
  const day = (d: Date) => new Intl.DateTimeFormat("en-CA", { timeZone: timezone }).format(d);
  const at = new Date(iso);
  const time = new Intl.DateTimeFormat("en-GB", { timeZone: timezone, hour: "2-digit", minute: "2-digit" }).format(at);
  return day(at) === day(new Date())
    ? time
    : `${new Intl.DateTimeFormat("en-GB", { timeZone: timezone, day: "numeric", month: "short" }).format(at)}, ${time}`;
}

/** Deliveries (P8-57, `Console / Deliveries`): every trip on any day, any combination of filters, saved views. */
export function DeliveriesPage(): ReactNode {
  const search = useSearch({ from: "/console/deliveries" });
  const navigate = useNavigate({ from: "/deliveries" });
  const format = useCompanyFormat();
  const { data: me } = useSuspenseQuery(meQuery);
  const canDispatch = me?.permissions.includes("deliveries.dispatch") ?? false;
  const [picking, setPicking] = useState(false);
  const [payerName, setPayerName] = useState<string | null>(null);
  const saved = useSavedViews("deliveries");
  const { sort, ...filters } = search;
  // Arriving with no filters opens the first view; picking All deliveries afterwards stays on all.
  const landed = useRef(Object.keys(filters).length === 0);
  useEffect(() => {
    if (!landed.current) return;
    landed.current = false;
    navigate({ search: (s: DeliverySearch) => ({ ...s, ...builtInViews(format.timezone)[0].search }), replace: true });
  }, [navigate, format.timezone]);

  const list = useCursorList({
    queryKey: ["deliveries", "list", search],
    fetchPage: async (cursor) => {
      const { data, error } = await api.GET("/v1/admin/dispatch/deliveries", {
        params: { query: { ...search, cursor, limit: 25 } },
      });
      if (error || !data) throw errorOf(error);
      return data;
    },
  });
  const totals = useQuery(summaryQuery(filters));
  const builtIn = builtInViews(format.timezone);
  const counts = useQueries({ queries: builtIn.map(({ search: { sort: _, ...f } }) => summaryQuery(f)) });
  const views = [...builtIn.map((v, i) => ({ ...v, count: counts[i].data?.count })), ...saved.views];
  const set = useCallback(
    (patch: Search) =>
      navigate({
        search: (s: DeliverySearch) =>
          Object.fromEntries(Object.entries({ ...s, ...patch }).filter(([, v]) => v !== undefined)) as DeliverySearch,
      }),
    [navigate],
  );
  const first = list.rows[0];
  const fleetFields = useFleetFields("Delivery");

  const fields: FilterField[] = [
    {
      id: "status",
      label: "Status",
      group: "Delivery",
      icon: <CircleDot {...ICON} />,
      kind: "options",
      param: "status",
      options: optionsOf(DELIVERY_STATUS),
    },
    datesField("scheduled", "Scheduled", "Delivery"),
    {
      id: "late",
      label: "Late",
      group: "Delivery",
      icon: <Clock {...ICON} />,
      kind: "flag",
      param: "late",
      value: "true",
    },
    {
      id: "incident",
      label: "Has an open incident",
      group: "Delivery",
      icon: <TriangleAlert {...ICON} />,
      kind: "flag",
      param: "incident",
      value: "open",
    },
    ...fleetFields,
    {
      id: "payer",
      label: "Customer or business",
      group: "Order",
      icon: <Users {...ICON} />,
      kind: "custom",
      params: ["customer_id", "business_id"],
      describe: (s) =>
        s.customer_id || s.business_id
          ? (payerName ?? (s.business_id ? first?.business?.name : first?.customer.name) ?? "the one chosen")
          : null,
    },
    {
      id: "payment",
      label: "Payment",
      group: "Order",
      icon: <Wallet {...ICON} />,
      kind: "options",
      param: "payment",
      options: optionsOf(PAYMENT_STATUS),
    },
  ];

  const t = totals.data;
  return (
    <>
      <PageHeader
        title="Deliveries"
        description="Every trip, on any day: where it stands, who carries it, what it holds."
        actions={canDispatch ? <Button onClick={() => navigate({ to: "/dispatch" })}>Open dispatch</Button> : undefined}
      />
      <ViewTabs
        views={views}
        current={viewOf(views, filters, FILTER_KEYS)}
        onPick={(v) => navigate({ search: { sort, ...v.search } as DeliverySearch })}
        onSave={(label) => saved.save(label, filters)}
        onDelete={(v) => saved.remove(v.id)}
      />
      <ListView
        caption="Deliveries"
        itemName={{ one: "delivery", other: "deliveries" }}
        rows={list.rows}
        rowKey={(d) => d.id}
        status={list.status}
        errorMessage={list.error ? messageFor(errorOf(list.error)) : undefined}
        onRetry={list.refetch}
        hasMore={list.hasMore}
        loadingMore={list.loadingMore}
        onLoadMore={list.loadMore}
        filtered={deliveryFilters.active(filters as DeliverySearch).length > 0}
        onResetFilters={() => navigate({ search: {} })}
        empty={{ title: "No deliveries yet", description: "Each order's deliveries show here once it is placed." }}
        toolbar={
          <div className={styles.filters}>
            <FilterBar
              fields={fields}
              search={search}
              onChange={set}
              format={format}
              query={{ param: "q", placeholder: "Search delivery or order number, customer, plate or driver" }}
              onCustom={(id) => id === "payer" && setPicking(true)}
              actions={
                <DropdownMenu
                  label={SORT[sort ?? "scheduled"]}
                  icon={<ArrowDownUp size={15} aria-hidden />}
                  items={(Object.keys(SORT) as (keyof typeof SORT)[]).map((k) => ({
                    label: SORT[k],
                    onSelect: () => set({ sort: k === "scheduled" ? undefined : k }),
                  }))}
                />
              }
            />
            {t ? (
              <p className={styles.summary} aria-live="polite">
                <strong>
                  {t.count.toLocaleString("en-GB")} {t.count === 1 ? "delivery" : "deliveries"}
                </strong>
                {t.litres ? (
                  <span>
                    Litres <b>{t.litres.toLocaleString("en-GB")} L</b>
                  </span>
                ) : null}
                <span>
                  Late <b className={t.late ? styles.danger : undefined}>{t.late}</b>
                </span>
                <span>
                  Open incidents <b className={t.open_incidents ? styles.warning : undefined}>{t.open_incidents}</b>
                </span>
              </p>
            ) : null}
          </div>
        }
        columns={[
          {
            key: "delivery_number",
            label: "Delivery",
            render: (d) => (
              <span className={styles.who}>
                <Link to={deliveryHref(d.order.id, d.id)} className={styles.link}>
                  {d.delivery_number}
                </Link>
                <span className={styles.muted}>{d.order.order_number}</span>
              </span>
            ),
          },
          {
            key: "scheduled_at",
            label: "Scheduled",
            render: (d) => (
              <span className={styles.who}>
                <span>{d.scheduled_at ? when(d.scheduled_at, format.timezone) : "Any time"}</span>
                {d.late ? (
                  <span className={styles.danger}>Late</span>
                ) : d.delivered_at ? (
                  <span className={styles.muted}>Delivered {when(d.delivered_at, format.timezone)}</span>
                ) : null}
              </span>
            ),
          },
          {
            key: "customer",
            label: "Customer · place",
            render: (d) => (
              <span className={styles.who}>
                <span>{d.business?.name ?? d.customer.name}</span>
                <span className={styles.muted}>
                  {[d.location?.label, d.zone?.name].filter(Boolean).join(" · ") || "No place"}
                </span>
              </span>
            ),
          },
          {
            key: "load",
            label: "Load",
            render: (d) => (
              <span className={styles.who}>
                <span>{d.litres ? `${d.litres.toLocaleString("en-GB")} L` : d.items}</span>
                <span className={styles.muted}>{d.fillsite?.name ?? "No fill site yet"}</span>
              </span>
            ),
          },
          {
            key: "truck",
            label: "Truck · driver",
            render: (d) =>
              d.truck || d.driver ? (
                <span className={styles.who}>
                  <span>{d.truck?.plate_number ?? "No truck"}</span>
                  <span className={styles.muted}>{d.driver?.name ?? "No driver"}</span>
                </span>
              ) : (
                <span className={styles.muted}>Not assigned</span>
              ),
          },
          {
            key: "status",
            label: "Status",
            render: (d) => (
              <span className={styles.who}>
                <span>
                  <Badge tone={DELIVERY_STATUS[d.status][1]}>{DELIVERY_STATUS[d.status][0]}</Badge>
                  {d.incident ? <TriangleAlert size={14} className={styles.danger} aria-label="Open incident" /> : null}
                </span>
                {d.held_reason || d.incident ? (
                  <span className={styles.muted}>{d.held_reason ?? d.incident?.type}</span>
                ) : null}
              </span>
            ),
          },
          { key: "amount", label: "Amount", numeric: true, render: (d) => formatMoney(d.amount, format) },
        ]}
      />
      {picking ? (
        <PayerFilterDialog
          title="Whose deliveries"
          description="Show only one customer's or one business's deliveries."
          action="Show their deliveries"
          onPick={(payer) => {
            setPicking(false);
            setPayerName(payer.label);
            set(
              "customer_id" in payer.billee
                ? { customer_id: payer.billee.customer_id, business_id: undefined }
                : { business_id: payer.billee.business_id, customer_id: undefined },
            );
          }}
          onClose={() => setPicking(false)}
        />
      ) : null}
    </>
  );
}
